import { marked } from 'marked'
import DOMPurify from 'dompurify'
import moment from 'moment'

// Same notice as Atlas's AI Insights.
export const DISCLAIMER_TEXT =
  'The insights presented here are generated by artificial intelligence and are intended to assist with analysis, not replace professional judgment. While we strive for accuracy, AI-generated content may contain errors, misinterpretations, or incomplete analysis. Please verify all data and conclusions independently before making business decisions.'

// Model output is untrusted: item descriptions in the prompt could steer it. Images are dropped
// too, since an image URL is the easiest way for injected text to send data elsewhere.
export function renderInsightsMarkdown(markdown) {
  if (!markdown) return ''
  const html = marked.parse(markdown, { gfm: true, async: false })
  return DOMPurify.sanitize(html, {
    USE_PROFILES: { html: true },
    FORBID_TAGS: ['img', 'style', 'form', 'input', 'button', 'iframe'],
    FORBID_ATTR: ['style']
  })
}

const integer = value => (value === null || value === undefined ? 'n/a' : Number(value).toLocaleString('en-US'))

// Percentages are rounded as the API rounds the report's data (_whole, _pct and _share in
// tpr/logic/ai_insights.py), so the tiles and the report always show the same numbers.

// Whole number, halves away from zero (Math.round alone takes -2.5 to -2). `|| 0` drops a -0.
const roundHalfUp = value => Math.sign(value) * Math.round(Math.abs(value)) || 0

// An average percentage. One that rounds to 0 but isn't 0 says which side it is on.
const percent = value => {
  if (value === null || value === undefined) return 'n/a'
  const v = Number(value)
  if (Number.isNaN(v)) return 'n/a'
  const whole = roundHalfUp(v)
  if (whole === 0 && v !== 0) return v > 0 ? 'under 1%' : 'just under 0%'
  return `${integer(whole)}%`
}

// Share of `whole`. A small share never shows as 0%, and only all of them shows as 100%.
const share = (part, whole) => {
  if (!whole) return null
  const pct = (Number(part || 0) / Number(whole)) * 100
  if (pct > 0 && pct < 0.5) return 'under 1%'
  if (pct >= 99.5 && pct < 100) return 'over 99%'
  return `${integer(roundHalfUp(pct))}%`
}

// As the API's checks_base_retail: every batch except perishable is checked.
const checksBaseRetail = batchType => (batchType || '').toUpperCase() !== 'PERISHABLE'

// A count, with its share of the whole batch underneath.
const countTile = (label, count, rows) => {
  const pct = share(count, rows)
  return { label, value: integer(count), detail: pct ? `${pct} of the batch` : '' }
}

// Headline figures, computed by the API, shown above the report. Every share is of all rows in
// the batch (items at ad-group level, item prices at store level), as in the report's data.
export function buildStatTiles(stats) {
  const overview = stats?.overview || {}
  const pricing = stats?.pricing || {}
  const rows = overview.rows

  const compliance = share(overview.enforce_compliance, rows)
  const aboveBaseRetail = checksBaseRetail(stats?.batch?.batch_type)
    ? countTile('Above Base Retail', overview.above_base_retail, rows)
    // A check that did not run shows no count, so a zero is never read as a pass.
    : { label: 'Above Base Retail', value: 'Not checked', detail: 'perishable batch' }

  // Averages only: the report calls out an average that a few outliers skew.
  return [
    countTile('Items Excluded', overview.excluded, rows),
    { label: 'Avg Discount', value: percent(pricing.discount_pct?.avg), detail: '' },
    { label: 'Avg Net Margin', value: percent(pricing.net_margin_pct?.avg), detail: '' },
    {
      label: '% of Compliance Items',
      value: compliance || 'n/a',
      detail: compliance ? `${integer(overview.enforce_compliance)} of ${integer(rows)}` : ''
    },
    aboveBaseRetail,
    countTile('Manual Overrides', overview.manual_override, rows)
  ]
}

export function formatTimestamp(value) {
  if (!value) return ''
  const parsed = moment(value)
  return parsed.isValid() ? parsed.format('MMM D, YYYY h:mm A') : ''
}

// Admin-only footer line: what the report cost and which models contributed.
export function formatUsage(result) {
  const usage = result?.usage
  if (!usage) return ''
  const parts = []
  parts.push(typeof usage.cost === 'number' ? `Cost $${usage.cost.toFixed(4)}` : 'Cost unavailable')
  parts.push(`${integer(usage.total_tokens)} tokens`)
  const models = result.models || []
  if (models.length > 1) parts.push(`${models.length} models${result.merged ? ', merged' : ''}`)
  else if (models.length === 1) parts.push(`1 model (${models[0]})`)
  return parts.join(' · ')
}

export function insightsErrorMessage(err) {
  const status = err?.response?.status
  const message = err?.response?.data?.message
  if (status === 403) return message || 'AI Insights are available for Price Pro stores only.'
  if (status === 404) return 'This batch could not be found. It may have been deleted.'
  if (status === 422) return 'The insights request was not valid for this batch.'
  if (status === 502) return message || 'AI Insights are temporarily unavailable. Please try again shortly.'
  if (status === 504 || err?.code === 'ECONNABORTED') return 'Generating insights took too long. Please try again.'
  if (message) return message
  if (!err?.response && err?.message === 'Network Error') return 'Could not reach the server. Please check your connection and try again.'
  return 'Failed to generate insights.'
}

export function slugify(text) {
  return (text || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'batch'
}
